// Helpers for gestures that must coexist with inner scroll views.

/** Attribute that stops sheet/layer drags from starting on an element (e.g. a scrubber). */
export const DRAG_IGNORE_ATTRIBUTE = 'data-drag-ignore';

function isScrollableY(element: Element): boolean {
  if (!(element instanceof HTMLElement)) return false;
  if (element.scrollHeight <= element.clientHeight + 1) return false;
  const overflow = getComputedStyle(element).overflowY;
  return overflow === 'auto' || overflow === 'scroll' || overflow === 'overlay';
}

/**
 * True when `target` sits inside a vertical scroller (up to and including
 * `root`) that is scrolled away from its top by more than `tolerance` px.
 */
export function isInsideScrolledScroller(target: EventTarget | null, root: Element, tolerance = 0.5): boolean {
  let node: Element | null = target instanceof Element ? target : null;
  while (node) {
    if (isScrollableY(node) && (node as HTMLElement).scrollTop > tolerance) return true;
    if (node === root) break;
    node = node.parentElement;
  }
  return false;
}

/** True when `target` (or an ancestor up to `root`) opts out of drag gestures. */
export function isDragIgnored(target: EventTarget | null, root: Element): boolean {
  let node: Element | null = target instanceof Element ? target : null;
  while (node) {
    if (node.hasAttribute(DRAG_IGNORE_ATTRIBUTE)) return true;
    if (node === root) break;
    node = node.parentElement;
  }
  return false;
}

/**
 * Reserves a page scroller's scrollbar gutter (`scrollbar-gutter: stable`)
 * unless its own CSS chose a value: with classic scrollbars the content no
 * longer shifts when it starts to overflow (skeleton → list), and headers that
 * reserve the same gutter (FrostedAppBar) line up with the content column.
 * No effect with overlay scrollbars or where scrollbars are hidden (touch).
 */
export function reserveScrollbarGutter(element: HTMLElement): void {
  if (element.style.scrollbarGutter) return;
  let computed = '';
  try {
    computed = getComputedStyle(element).scrollbarGutter ?? '';
  } catch {
    /* detached element */
  }
  if (computed && computed !== 'auto') return;
  element.style.scrollbarGutter = 'stable';
}
